import React, { useState, useEffect } from 'react';
import {
    AppBar, Toolbar, Typography, Box, Avatar, Menu, MenuItem,
    IconButton, Drawer, List, ListItemIcon,
    ListItemText, useMediaQuery, useTheme, Select, FormControl,
    Divider, ListItemButton, Tooltip, Collapse, Skeleton
} from '@mui/material';

import { alpha } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import CategoryIcon from '@mui/icons-material/Category';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import InsertChartIcon from '@mui/icons-material/InsertChartOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import RepeatIcon from '@mui/icons-material/Repeat';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import ManageWorkspaceDialog from './ledger/ManageWorkspaceDialog';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DescriptionIcon from '@mui/icons-material/Description';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PVLogo from './PVLogo';
import useTransientHighlight from '../hooks/useTransientHighlight';
import PulseHighlight from './common/PulseHighlight';
import { useTranslation } from 'react-i18next';
import { LanguageMenuButton } from './LanguageSelector';

const DRAWER_WIDTH = 280;

const Layout = ({ children }) => {
    const { t } = useTranslation();
    const { logout, currentUser, setCurrentUser, api } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [anchorEl, setAnchorEl] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [ledgers, setLedgers] = useState([]);
    const [investmentsOpen, setInvestmentsOpen] = useState(true);

    const match = location.pathname.match(/\/ledger\/([a-zA-Z0-9_-]+)/);
    const currentLedgerFromUrl = match ? match[1] : null;
    const activeLedgerId = currentLedgerFromUrl || (currentUser?.last_ledger_id || null);

    const isInvestmentsActive = Boolean(activeLedgerId && location.pathname.startsWith(`/ledger/${activeLedgerId}/investments`));

    useEffect(() => {
        if (isInvestmentsActive) {
            setInvestmentsOpen(true);
        }
    }, [isInvestmentsActive]);

    useEffect(() => {
        if (currentLedgerFromUrl && currentUser && currentUser.last_ledger_id !== currentLedgerFromUrl) {
            api.put('/users/me/last-ledger', { ledger_id: currentLedgerFromUrl })
                .then(() => {
                    if (setCurrentUser) {
                        setCurrentUser(prev => prev ? ({ ...prev, last_ledger_id: currentLedgerFromUrl }) : prev);
                    }
                })
                .catch(err => {
                    console.error("Failed to update last accessed ledger on server", err);
                });
        }
    }, [currentLedgerFromUrl, currentUser, api, setCurrentUser]);

    const fetchLedgers = React.useCallback(async () => {
        try {
            const response = await api.get('/ledger/ledgers');
            const fetchedLedgers = response.data;
            setLedgers(fetchedLedgers);

            if (fetchedLedgers.length > 0) {
                const activeMatch = location.pathname.match(/\/ledger\/([a-zA-Z0-9_-]+)/);
                const currentActiveId = activeMatch ? activeMatch[1] : (currentUser?.last_ledger_id || null);

                const isValidLedger = currentActiveId && fetchedLedgers.some(l => l.id.toString() === currentActiveId.toString());

                if (!isValidLedger) {
                    const fallbackLedgerId = (currentUser?.last_ledger_id && fetchedLedgers.some(l => l.id.toString() === currentUser.last_ledger_id.toString()))
                        ? currentUser.last_ledger_id
                        : fetchedLedgers[0].id.toString();
                    if (activeMatch) {
                        const subPath = location.pathname.replace(/\/ledger\/[a-zA-Z0-9_-]+/, '');
                        navigate(`/ledger/${fallbackLedgerId}${subPath}`, { replace: true });
                    }
                }
            } else {
                navigate('/', { replace: true });
            }
        } catch (err) {
            console.error("Failed to fetch ledgers in Layout", err);
        }
    }, [api, location.pathname, navigate, currentUser]);

    useEffect(() => {
        if (currentUser) {
            fetchLedgers();
        }
    }, [currentUser, fetchLedgers]);

    const { isHighlighted: highlightDropdown, message: highlightMsg, clearHighlight } = useTransientHighlight({
        eventName: 'ledgerChanged',
        filter: (e) => {
            fetchLedgers();
            return e?.detail?.action === 'accept';
        },
        defaultMessage: 'New shared book added!',
        durationMs: 5000,
        onTrigger: () => {
            if (isMobile) setDrawerOpen(true);
        }
    });

    const [manageDialogOpen, setManageDialogOpen] = useState(false);

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const handleLogout = () => {
        if (isMobile) setDrawerOpen(false);
        handleMenuClose();
        logout();
    };

    const handleNavItemClick = (path) => {
        if (isMobile) setDrawerOpen(false);
        if (path) navigate(path);
    };

    const globalMenuItems = [
        { text: t('Profile'), icon: <PersonIcon />, path: '/profile' },
        { text: t('Credit Cards'), icon: <CreditCardIcon />, path: '/credit-cards' },
        { text: t('Categories'), icon: <CategoryIcon />, path: '/categories' },
        { text: t('Documents'), icon: <DescriptionIcon />, path: '/documents' },
        { text: t('Settings'), icon: <SettingsIcon />, path: '/settings' },
    ];

    if (currentUser?.is_super_admin) {
        globalMenuItems.push({ text: t('Admin'), icon: <AdminPanelSettingsIcon sx={{ color: 'secondary.main' }} />, path: '/admin' });
    }


    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'surface-container-low' }}>
            <Box
                sx={{ p: 3, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                onClick={() => handleNavItemClick('/')}
            >
                <PVLogo sx={{ width: 36, height: 36 }} />
                <Typography variant="h6" noWrap sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: '-0.03em' }}>
                    PV Books
                </Typography>
            </Box>

            <Box sx={{ px: 2, pt: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <PulseHighlight active={highlightDropdown} message={highlightMsg}>
                        <FormControl fullWidth size="small">
                            <Select
                                value={activeLedgerId || ''}
                                onOpen={clearHighlight}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleNavItemClick(`/ledger/${e.target.value}`);
                                    }
                                }}
                                renderValue={(selectedId) => {
                                    const selectedLedger = ledgers.find(l => l.id.toString() === selectedId);
                                    if (!selectedLedger) return '';
                                    return selectedLedger.owner_name
                                        ? `${selectedLedger.name} (Shared)`
                                        : selectedLedger.name;
                                }}
                                sx={{
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    bgcolor: 'surface-container-lowest',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(81, 95, 116, 0.2)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#006D43',
                                    }
                                }}
                            >
                                {ledgers.map(l => (
                                    <MenuItem key={l.id} value={l.id.toString()} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                             {l.name}
                                        </Typography>
                                        {l.owner_name && (
                                            <Tooltip title={l.owner_email} placement="right" arrow>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mt: 0.2 }}>
                                                    Shared by {l.owner_name}
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </PulseHighlight>
                </Box>
                <Tooltip title={t('Manage Workspaces')} arrow placement="top">
                    <IconButton
                        onClick={() => {
                            if (isMobile) setDrawerOpen(false);
                            setManageDialogOpen(true);
                        }}
                        size="small"
                        sx={{
                            borderRadius: 1,
                            p: 1,
                            color: 'text.secondary',
                            border: '1px solid rgba(81, 95, 116, 0.2)',
                            bgcolor: 'surface-container-lowest',
                            height: 40,
                            width: 40,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                bgcolor: 'rgba(81, 95, 116, 0.05)',
                                borderColor: '#006D43',
                                color: 'primary.main',
                            }
                        }}
                    >
                        <AccountTreeIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, mt: 5 }}>
                {activeLedgerId && (
                    <List sx={{ pt: 0 }}>
                        <ListItemButton
                            selected={location.pathname === `/ledger/${activeLedgerId}/dashboard`}
                            onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/dashboard`)}
                            sx={{ borderRadius: 1, mb: 1.25 }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}><InsertChartIcon /></ListItemIcon>
                            <ListItemText primary={t('Dashboard')} primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        <ListItemButton
                            selected={location.pathname === `/ledger/${activeLedgerId}`}
                            onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}`)}
                            sx={{ borderRadius: 1, mb: 1.25 }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}><ReceiptLongIcon /></ListItemIcon>
                            <ListItemText primary={t('Transactions')} primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        <ListItemButton
                            selected={location.pathname === `/ledger/${activeLedgerId}/recurring`}
                            onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/recurring`)}
                            sx={{ borderRadius: 1, mb: 1.25 }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}><RepeatIcon /></ListItemIcon>
                            <ListItemText primary={t('Recurring')} primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        <ListItemButton
                            selected={location.pathname === `/ledger/${activeLedgerId}/budget`}
                            onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/budget`)}
                            sx={{ borderRadius: 1, mb: 1.25 }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}><AccountBalanceIcon /></ListItemIcon>
                            <ListItemText primary={t('Budget')} primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        <ListItemButton
                            selected={location.pathname === `/ledger/${activeLedgerId}/cashflow`}
                            onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/cashflow`)}
                            sx={{ borderRadius: 1, mb: 1.25 }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}><CompareArrowsIcon /></ListItemIcon>
                            <ListItemText primary={t('Cash Flow')} primaryTypographyProps={{ fontWeight: 600 }} />
                        </ListItemButton>

                        {/* Investments Parent Item */}
                        <ListItemButton
                            selected={isInvestmentsActive}
                            onClick={() => {
                                setInvestmentsOpen(prev => !prev);
                                if (!isInvestmentsActive) {
                                    handleNavItemClick(`/ledger/${activeLedgerId}/investments`);
                                }
                            }}
                            sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}><ShowChartIcon color="primary" /></ListItemIcon>
                            <ListItemText primary={t('Investments')} primaryTypographyProps={{ fontWeight: 600 }} />
                            {investmentsOpen ? <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
                        </ListItemButton>

                        {/* Investments Nested Sub-Navigation */}
                        <Collapse in={investmentsOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{ mb: 1 }}>
                                {/* 1. Dashboard Overview */}
                                <ListItemButton
                                    selected={location.pathname === `/ledger/${activeLedgerId}/investments` || location.pathname === `/ledger/${activeLedgerId}/investments/dashboard`}
                                    onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/investments`)}
                                    sx={{ pl: 4.5, py: 0.75, borderRadius: 1, mb: 0.4 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}><DashboardIcon fontSize="small" sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                                    <ListItemText primary={t('Portfolio Overview')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
                                </ListItemButton>

                                {/* 2. Holdings & Positions */}
                                <ListItemButton
                                    selected={location.pathname === `/ledger/${activeLedgerId}/investments/holdings`}
                                    onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/investments/holdings`)}
                                    sx={{ pl: 4.5, py: 0.75, borderRadius: 1, mb: 0.4 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}><ShowChartIcon fontSize="small" sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                                    <ListItemText primary={t('Holdings & ACB')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
                                </ListItemButton>

                                {/* 3. Tax Reporting */}
                                <ListItemButton
                                    selected={location.pathname === `/ledger/${activeLedgerId}/investments/tax` || location.pathname === `/ledger/${activeLedgerId}/investments/realized-gains`}
                                    onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/investments/tax`)}
                                    sx={{ pl: 4.5, py: 0.75, borderRadius: 1, mb: 0.4 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}><RequestQuoteIcon fontSize="small" sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                                    <ListItemText primary={t('Tax & Filing')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
                                </ListItemButton>

                                {/* 4. Trade History */}
                                <ListItemButton
                                    selected={location.pathname === `/ledger/${activeLedgerId}/investments/trades`}
                                    onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/investments/trades`)}
                                    sx={{ pl: 4.5, py: 0.75, borderRadius: 1 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}><ReceiptLongIcon fontSize="small" sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                                    <ListItemText primary={t('Trades & Activities')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
                                </ListItemButton>
                            </List>
                        </Collapse>

                        <ListItemButton
                            selected={location.pathname === `/ledger/${activeLedgerId}/advisor`}
                            onClick={() => handleNavItemClick(`/ledger/${activeLedgerId}/advisor`)}
                            sx={{
                                borderRadius: 1,
                                mb: 1.25,
                                color: '#006D43',
                                '&.Mui-selected': {
                                    bgcolor: 'rgba(0, 109, 67, 0.12)',
                                    color: '#006D43',
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40, color: '#006D43' }}>
                                <AutoAwesomeIcon />
                            </ListItemIcon>
                            <ListItemText primary={t('Advisor')} primaryTypographyProps={{ fontWeight: 700 }} />
                        </ListItemButton>
                    </List>
                )}
                <Box sx={{ height: 16 }} />
            </Box>

            <Box sx={{ p: 2 }}>
                <ListItemButton
                    onClick={handleLogout}
                    sx={{ borderRadius: 1, color: 'error.main' }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}><LogoutIcon /></ListItemIcon>
                    <ListItemText primary={t('Logout')} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'surface' }}>
            {/* Sidebar */}
            <Box
                component="nav"
                sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
            >
                <Drawer
                    variant={isMobile ? 'temporary' : 'permanent'}
                    open={isMobile ? drawerOpen : true}
                    onClose={toggleDrawer(false)}
                    ModalProps={{ keepMounted: true }} // Better open performance on mobile.
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                            borderRight: 'none',
                            bgcolor: 'surface-container-low'
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Main Content Area */}
            <Box component="main" sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
                maxWidth: { xs: '100vw', md: `calc(100% - ${DRAWER_WIDTH}px)` }
            }}>
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        top: 0,
                        zIndex: (theme) => theme.zIndex.appBar,
                        bgcolor: (theme) => alpha(theme.palette.background.default, 0.8),
                        backdropFilter: 'blur(20px)',
                        color: 'text.primary',
                        borderBottom: 'none'
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={toggleDrawer(!drawerOpen)}
                            sx={{
                                mr: 2,
                                color: 'primary.main',
                                display: { md: 'none' }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                flexGrow: 1,
                                fontWeight: 700,
                                color: 'primary.main',
                                letterSpacing: '-0.02em',
                                cursor: 'pointer',
                            }}
                            onClick={() => navigate('/')}
                        >
                            {/* In desktop mode, title shrinks. On mobile, we keep it. */}
                            {isMobile ? 'PV Books' : ''}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                            <LanguageMenuButton />
                            {currentUser && (
                                <>
                                    <NotificationCenter />
                                    {currentUser.first_name || (!currentUser.username?.includes('@') ? currentUser.username : null) ? (
                                        <>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                                                {currentUser.first_name || currentUser.username}
                                            </Typography>
                                            <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.5 }} aria-label="Account settings">
                                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '1rem', fontWeight: 700 }}>
                                                    {(currentUser.first_name || currentUser.username).charAt(0).toUpperCase()}
                                                </Avatar>
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Skeleton variant="text" width={60} height={20} sx={{ display: { xs: 'none', sm: 'block' } }} />
                                            <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.5 }} aria-label="Account settings">
                                                <Skeleton variant="circular" width={36} height={36} />
                                            </IconButton>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 12px 32px rgba(28, 28, 24, 0.08))',
                                    mt: 1.5,
                                    width: 220,
                                    borderRadius: 1
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            {globalMenuItems.map((item) => (
                                <MenuItem key={item.text} onClick={() => { handleMenuClose(); navigate(item.path); }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {React.cloneElement(item.icon, { sx: { mr: 2, fontSize: 20, color: 'text.secondary' } })}
                                        {item.text}
                                    </Box>
                                </MenuItem>
                            ))}
                            <Divider sx={{ my: 1, opacity: 0.5 }} />
                            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                                <LogoutIcon sx={{ mr: 2, fontSize: 20 }} /> {t('Logout')}
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>

                {/* Page Content */}
                <Box sx={{ flexGrow: 1, overflowX: 'hidden', minWidth: 0, width: '100%' }}>
                    {children}
                </Box>
            </Box>

            <ManageWorkspaceDialog
                open={manageDialogOpen}
                onClose={() => setManageDialogOpen(false)}
                ledgers={ledgers}
                onRefresh={fetchLedgers}
            />
        </Box>
    );
};

export default Layout;
